import { PATH_CONTRACTOR, PATH_CONTRACTORS } from "@/constants";
import { fetchContractorsList } from "@/services/contractors";
import { forwardRef, useCallback } from "react";
import { ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";
import { FilterType } from "@/lib/filters";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { ReadContractor } from "@/types/contractor";
import { EntityListLink } from "./EntityListLink";
import { generatePath } from "react-router-dom";
import { NameWithSuffix } from "./NameWithRef";

interface ContractorSelectProps extends ControllerRenderProps {
  className?: string;
  inDialog?: boolean;
  clear?: boolean;
  showLink?: boolean;
  placeholder?: string;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, ContractorSelectProps>(
  function ContractorSelect(
    { value, inDialog, showLink = true, defaultItem, ...props },
    ref,
  ) {
    const { t } = useTranslation();
    const name = "contractorSelect";
    const options = useCreateDataTableOptions(name);
    const queryKey = useDynamicQueryParams<ReadContractor>(name, options);

    const renderContractorName = ({
      full_name,
      name,
      user_ref_number,
    }: ReadContractor & Option) => (
      <NameWithSuffix name={full_name ?? name} suffix={user_ref_number} />
    );

    const onFilter = useCallback(
      (filterValue: string) => {
        options.setFilterValue("full_name", {
          value: filterValue,
          filter: FilterType.IncludesString,
        });
      },
      [options],
    );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            fetchContractorsList({
              filters: options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("contractorSelect.placeholder")}
          loadingLabel={t("contractorSelect.loadingContractors")}
          noResultsLabel={t("contractorSelect.noResults")}
          searchLabel={t("contractorSelect.search")}
          enableFilter={true}
          onFilter={onFilter}
          renderName={renderContractorName}
          className="w-full"
          {...props}
          value={value}
          ref={ref}
          inDialog={inDialog}
          defaultItem={defaultItem}
        />
        {showLink && (
          <EntityListLink
            path={
              value
                ? generatePath(PATH_CONTRACTOR, { contractorId: value })
                : PATH_CONTRACTORS
            }
            label={t("contractorSelect.link")}
          />
        )}
      </span>
    );
  },
);
